import {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { ORDER_TABLE_FIELDS as OK, ORDER_STATUS_CLASSES } from 'utils/constants'
import {
  conditionalDateFormatter,
  convertTo12HourFormat,
} from 'utils/helpers/date'
import { OrderProductType, PowerBiSync } from 'types/order'
import OrderSourceChip from './orderSourceChip'

const valueFormatter = (params: ValueFormatterParams) => params.value ?? '-'

export const getStatus = (value?: boolean) => {
  return value === true ? 'Success' : value === false ? 'Fail' : '-'
}

export const booleanValueFormatter = (params: ICellRendererParams) => {
  return params.value === true ? 'Yes' : params.value === false ? 'No' : '-'
}

export const columns = [
  { headerName: OK.ORDER_NO.label, field: OK.ORDER_NO.value, minWidth: 180 },
  {
    headerName: OK.INVOICE_NO.label,
    field: OK.INVOICE_NO.value,
    minWidth: 200,
  },
  {
    headerName: OK.ORDER_SHORTCODE.label,
    field: OK.ORDER_SHORTCODE.value,
    minWidth: 150,
  },
  {
    headerName: OK.AGGREGATOR_CODE.label,
    field: OK.AGGREGATOR_CODE.value,
    minWidth: 130,
  },
  {
    headerName: OK.AGGREGATOR_NAME.label,
    field: OK.AGGREGATOR_NAME.value,
    minWidth: 130,
  },
  {
    headerName: OK.STORE_CODE.label,
    field: OK.STORE_CODE.value,
    minWidth: 130,
  },
  {
    headerName: OK.STORE_NAME.label,
    field: OK.STORE_NAME.value,
    minWidth: 150,
  },
  {
    headerName: OK.ORDER_PLACE_DATETIME.label,
    field: OK.ORDER_PLACE_DATETIME.value,
    minWidth: 170,
    valueFormatter: conditionalDateFormatter,
  },
  {
    headerName: OK.ORDER_POSTING_DATE.label,
    field: OK.ORDER_POSTING_DATE.value,
    minWidth: 170,
    valueFormatter: (params: ValueFormatterParams) =>
      convertTo12HourFormat(params.value),
  },
  {
    headerName: OK.PLANNED_DELIVERY_DATE.label,
    field: OK.PLANNED_DELIVERY_DATE.value,
    minWidth: 170,
    valueFormatter: conditionalDateFormatter,
  },
  {
    headerName: OK.PREPARED_BY.label,
    field: OK.PREPARED_BY.value,
    valueFormatter,
  },
  {
    headerName: OK.EMP_ID.label,
    field: OK.EMP_ID.value,
    valueFormatter,
  },
  {
    headerName: OK.DELIVERY_METHOD.label,
    field: OK.DELIVERY_METHOD.value,
    valueFormatter,
  },
  {
    headerName: 'Order Source',
    field: 'orderSource',
    minWidth: 140,
    cellRenderer: (params: ICellRendererParams) => {
      return (
        <OrderSourceChip
          isGrubtechOrder={params.data?.isGrubtechOrder}
          isPosOrder={params.data?.isPosOrder}
          aggregatorCode={params.data?.Aggregator_Code}
          aggregatorName={params.data?.Aggregator_Name}
        />
      )
    },
  },
  {
    headerName: OK.IS_POS_ORDER.label,
    field: OK.IS_POS_ORDER.value,
    cellRenderer: booleanValueFormatter,
  },
  {
    headerName: OK.IS_GRUBTECH_ORDER.label,
    field: OK.IS_GRUBTECH_ORDER.value,
    cellRenderer: booleanValueFormatter,
  },
  {
    headerName: 'Order Total',
    field: OK.ORDER_VALUE_INCLV_TAX.value,
    valueFormatter,
  },
  {
    headerName: OK.CUSTOMER_NAME.label,
    field: OK.CUSTOMER_NAME.value,
    minWidth: 120,
    valueFormatter,
  },
  { headerName: OK.AREA.label, field: OK.AREA.value, valueFormatter },
  {
    headerName: OK.CONTACT.label,
    field: OK.CONTACT.value,
    minWidth: 130,
    valueFormatter,
  },
  {
    headerName: OK.STATUS.label,
    field: OK.STATUS.value,
    minWidth: 200,
    cellRenderer: ({ value }: ICellRendererParams) => {
      return (
        <span
          className={`rounded-lg px-2 py-1 font-sans text-sm font-bold  ${
            ORDER_STATUS_CLASSES[value!]
          }`}
        >
          {value}
        </span>
      )
    },
  },
  {
    headerName: OK.ERP_SYNC.label,
    field: OK.ERP_SYNC.value,
    minWidth: 120,
    cellRenderer: (params: ICellRendererParams) => {
      return (
        <span
          className={`rounded-lg px-2 py-1 text-sm ${
            params?.value ? 'bg-green-400 text-black' : 'bg-red-500  text-white'
          }`}
        >
          {getStatus(params?.value)}
        </span>
      )
    },
  },
  {
    headerName: OK.LAST_MILE.label,
    field: OK.LAST_MILE.value,
    valueFormatter,
  },
  {
    headerName: OK.LAST_MILE_SYNC.label,
    field: OK.LAST_MILE_SYNC.value,
    minWidth: 120,
    cellRenderer: (params: ICellRendererParams) => {
      return (
        <span
          className={`rounded-lg px-2 py-1 text-sm ${
            params?.value ? 'bg-green-400 text-black' : 'bg-red-500  text-white'
          }`}
        >
          {getStatus(params?.value)}
        </span>
      )
    },
  },
  {
    headerName: OK.ON_HOLD_ORDER.label,
    field: OK.ON_HOLD_ORDER.value,
    minWidth: 120,
    cellRenderer: (params: ICellRendererParams) => {
      return (
        <span
          className={`rounded-lg px-2 py-1 text-sm ${
            params?.value
              ? 'bg-yellow-400 text-black'
              : 'bg-gray-500  text-white'
          }`}
        >
          {params?.value === false ? 'False' : 'True'}
        </span>
      )
    },
  },
  {
    headerName: OK.POWER_BI_SYNC.label,
    field: OK.POWER_BI_SYNC.value,
    minWidth: 140,
    cellRenderer: (params: ICellRendererParams) => {
      const sync: PowerBiSync | undefined = params?.value
      if (!sync) {
        return (
          <span className="rounded-lg bg-gray-200 px-2 py-1 text-sm text-gray-600">
            N/A
          </span>
        )
      }
      if (sync.syncStatus === 'SYNCED') {
        const tooltip = sync.lastSyncedAt
          ? `Last synced: ${new Date(sync.lastSyncedAt).toLocaleString()}`
          : 'Synced'
        return (
          <span
            title={tooltip}
            className="rounded-lg bg-green-400 px-2 py-1 text-sm text-black"
          >
            Synced
          </span>
        )
      }
      const tooltip = `Awaiting sync — Event: ${sync.eventType}`
      return (
        <span
          title={tooltip}
          className="rounded-lg bg-yellow-400 px-2 py-1 text-sm text-black"
        >
          Pending
        </span>
      )
    },
  },
] as ColDef[]

// Orders products so every bundle parent line is immediately followed by
// the child lines it expanded into (matched via bundleGroupId), while
// leaving normal (non-bundle) products in their original order untouched.
export const groupBundleProducts = (products: OrderProductType[]) => {
  const childrenByGroup = new Map<string, OrderProductType[]>()

  products.forEach((product) => {
    if (product.isBundleChild && product.bundleGroupId) {
      const group = childrenByGroup.get(product.bundleGroupId) ?? []
      group.push(product)
      childrenByGroup.set(product.bundleGroupId, group)
    }
  })

  const ordered: OrderProductType[] = []

  products.forEach((product) => {
    if (product.isBundleChild) return

    ordered.push(product)

    if (product.isBundleParent && product.bundleGroupId) {
      ordered.push(...(childrenByGroup.get(product.bundleGroupId) ?? []))
    }
  })

  return ordered
}

export const FILTER_FIELD_ORDERS = 'orderFilters'

export const FILTER_FIELD_ECOM_PRICING = 'ecomPricingFilters'

export const RMA_REFUND_TYPES = [
  {
    label: 'No Refund',
    value: 'NO_CASH',
  },
]
